'use client';

import {
    EditableSettingsTabId,
    QUESTIONNAIRE_CHANNEL_OPTIONS,
    QUESTIONNAIRE_GENERAL_SETTINGS_OPTIONS,
    QUESTIONNAIRE_RUNTIME_MARKETPLACE_OPTIONS,
} from '@/components/settings-questionnaire-modal/settings-questionnaire.schema';
import { SettingsQuestionnaireModalControllerBindings } from '@/components/settings-questionnaire-modal/use-settings-questionnaire-modal-controller';
import Input from '@/ui/Input';
import Textarea from '@/ui/Textarea';
import clsx from 'clsx';
import { FC, ReactNode } from 'react';

type SettingsQuestionnaireSectionBindings = Pick<
    SettingsQuestionnaireModalControllerBindings,
    | 'formState'
    | 'questions'
    | 'completionProgress'
    | 'onStoreNameChanged'
    | 'onCompanyInfoLinkChanged'
    | 'onEmailChanged'
    | 'onCommentsChanged'
    | 'onAnswerChanged'
    | 'onGeneralSettingChanged'
    | 'onChannelChanged'
    | 'onMarketplaceReviewImportLimitChanged'
>;

type SettingsQuestionnaireSectionProps = SettingsQuestionnaireSectionBindings & {
    activeTab: EditableSettingsTabId;
    idPrefix?: string;
};

export const SettingsSectionCard: FC<{ title: string; description?: string; children: ReactNode }> = ({
    title,
    description,
    children,
}) => {
    return (
        <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="space-y-1">
                <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
                {description ? <p className="text-xs text-slate-500">{description}</p> : null}
            </div>
            <div className="mt-4">{children}</div>
        </section>
    );
};

export const SettingsCheckboxCard: FC<{
    label: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
    hint?: string;
}> = ({ label, checked, onChange, disabled, hint }) => {
    return (
        <label
            className={clsx(
                'flex items-start gap-3 rounded-lg border p-3',
                disabled ? 'cursor-not-allowed border-slate-200 bg-slate-50' : 'cursor-pointer border-slate-200',
            )}
        >
            <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-blue-600"
                checked={checked}
                disabled={disabled}
                onChange={(event) => onChange(event.target.checked)}
            />
            <span className="min-w-0">
                <span className="block text-sm text-slate-900">
                    {label}
                    {hint ? <span className="ml-2 text-xs text-slate-500">({hint})</span> : null}
                </span>
            </span>
        </label>
    );
};

export const SettingsQuestionnaireSection: FC<SettingsQuestionnaireSectionProps> = ({
    activeTab,
    idPrefix = 'questionnaire',
    formState,
    questions,
    completionProgress,
    onStoreNameChanged,
    onCompanyInfoLinkChanged,
    onEmailChanged,
    onCommentsChanged,
    onAnswerChanged,
    onGeneralSettingChanged,
    onChannelChanged,
    onMarketplaceReviewImportLimitChanged,
}) => {
    const storeNameInputId = `${idPrefix}-store-name`;
    const companyInfoLinkInputId = `${idPrefix}-company-link`;
    const emailInputId = `${idPrefix}-email`;
    const commentsInputId = `${idPrefix}-comments`;

    if (activeTab === 'general') {
        return (
            <div className="space-y-4">
                <SettingsSectionCard title="Компания и продукт">
                    <div className="space-y-4">
                        <section className="space-y-2">
                            <label className="text-sm font-medium text-slate-900" htmlFor={storeNameInputId}>
                                Название компании / магазина
                            </label>
                            <Input
                                id={storeNameInputId}
                                aria-label="Название компании"
                                value={formState.storeName}
                                onChange={(event) => onStoreNameChanged(event.target.value)}
                                placeholder="Название компании"
                            />
                        </section>

                        <section className="space-y-2">
                            <label className="text-sm font-medium text-slate-900" htmlFor={companyInfoLinkInputId}>
                                Ссылка на продукт
                            </label>
                            <Input
                                id={companyInfoLinkInputId}
                                aria-label="Ссылка на продукт"
                                value={formState.companyInfoLink}
                                onChange={(event) => onCompanyInfoLinkChanged(event.target.value)}
                                placeholder="https://..."
                            />
                        </section>

                        <section className="space-y-2">
                            <label className="text-sm font-medium text-slate-900" htmlFor={emailInputId}>
                                E-mail
                            </label>
                            <Input
                                id={emailInputId}
                                aria-label="E-mail клиента"
                                type="email"
                                value={formState.email}
                                onChange={(event) => onEmailChanged(event.target.value)}
                                placeholder="client@example.com"
                            />
                        </section>
                    </div>
                </SettingsSectionCard>

                <SettingsSectionCard title="Права и автоматизация">
                    <div className="space-y-3">
                        {QUESTIONNAIRE_GENERAL_SETTINGS_OPTIONS.map((option) => (
                            <SettingsCheckboxCard
                                key={option.id}
                                label={option.label}
                                checked={formState.settings.general[option.id]}
                                onChange={(checked) => onGeneralSettingChanged(option.id, checked)}
                            />
                        ))}
                    </div>
                </SettingsSectionCard>
            </div>
        );
    }

    if (activeTab === 'channels') {
        return (
            <SettingsSectionCard title="Доступные каналы">
                <div className="space-y-3">
                    {QUESTIONNAIRE_CHANNEL_OPTIONS.map((option) => (
                        <SettingsCheckboxCard
                            key={option.id}
                            label={option.label}
                            hint={option.hint}
                            checked={
                                option.id === 'yandexMarket'
                                    ? false
                                    : formState.settings.marketplaces[option.id].enabled
                            }
                            disabled={option.disabled}
                            onChange={(checked) => onChannelChanged(option.id, checked)}
                        />
                    ))}
                </div>
            </SettingsSectionCard>
        );
    }

    if (activeTab === 'review-import') {
        const enabledMarketplaceOptions = QUESTIONNAIRE_RUNTIME_MARKETPLACE_OPTIONS.filter(
            (option) => formState.settings.marketplaces[option.id].enabled,
        );
        const reviewImportLimitMax = formState.settings.marketplaceSettingsConstraints.reviewImportLimitMax;

        return (
            <SettingsSectionCard title="Импорт отзывов">
                {enabledMarketplaceOptions.length === 0 ? (
                    <p className="text-sm text-slate-500">Выберите маркетплейс на шаге «Каналы».</p>
                ) : (
                    <div className="space-y-4">
                        {enabledMarketplaceOptions.map((option) => {
                            const inputId = `${idPrefix}-review-import-${option.id}`;
                            const limit = formState.settings.marketplaces[option.id].preExistingReviewImportLimit;

                            return (
                                <section
                                    key={option.id}
                                    className="grid gap-2 rounded-lg border border-slate-200 p-3 sm:grid-cols-[180px_minmax(0,1fr)] sm:items-center"
                                >
                                    <div className="text-sm font-medium text-slate-900">{option.label}</div>
                                    <label className="space-y-1 text-sm text-slate-700" htmlFor={inputId}>
                                        <span className="font-medium text-slate-900">Количество отзывов</span>
                                        <Input
                                            id={inputId}
                                            aria-label={`Количество отзывов ${option.label}`}
                                            type="number"
                                            inputMode="numeric"
                                            min={0}
                                            max={reviewImportLimitMax}
                                            step={1}
                                            value={limit ?? ''}
                                            onChange={(event) =>
                                                onMarketplaceReviewImportLimitChanged(
                                                    option.id,
                                                    event.currentTarget.value,
                                                )
                                            }
                                        />
                                    </label>
                                </section>
                            );
                        })}
                    </div>
                )}
            </SettingsSectionCard>
        );
    }

    return (
        <div className="space-y-4">
            <SettingsSectionCard
                title="Стратегия работы с отзывами"
                description={`Можно заполнить частично. Заполнено ключевых полей: ${completionProgress}`}
            >
                <div className="space-y-4">
                    {questions.map((question) => (
                        <fieldset key={question.id} className="rounded-lg border border-slate-200 p-3">
                            <legend className="px-1 text-sm font-medium text-slate-900">
                                {question.index}. {question.title}
                            </legend>
                            <div className="mt-2 space-y-2">
                                {question.options.map((option) => {
                                    const isChecked = formState.answers[question.id] === option.value;

                                    return (
                                        <label
                                            key={option.value}
                                            className={clsx(
                                                'flex gap-2 items-start rounded-md border p-2 cursor-pointer',
                                                isChecked
                                                    ? 'border-blue-500 bg-blue-50'
                                                    : 'border-slate-200 hover:border-slate-300',
                                            )}
                                        >
                                            <input
                                                type="radio"
                                                className="mt-0.5"
                                                name={question.id}
                                                checked={isChecked}
                                                onChange={() => onAnswerChanged(question.id, option.value)}
                                            />
                                            <span className="text-xs text-slate-800">{option.label}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </fieldset>
                    ))}
                </div>
            </SettingsSectionCard>

            <SettingsSectionCard title="Комментарии и пожелания">
                <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-900" htmlFor={commentsInputId}>
                        Комментарии и пожелания
                    </label>
                    <Textarea
                        id={commentsInputId}
                        ariaLabel="Комментарии и пожелания"
                        value={formState.comments}
                        setValue={onCommentsChanged}
                        className="border border-slate-200 min-h-24"
                        placeholder="Ваши комментарии"
                        rows={4}
                    />
                </div>
            </SettingsSectionCard>
        </div>
    );
};
